import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiPlus, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import '../styles/AvailabilityManagement.css';

/** Editable schedule states (leave/working are auto-derived). */
const EDIT_STATES = [null, 'available', 'unavailable'];
const STATE_LABEL = {
  available: 'Available',
  unavailable: 'Unavailable',
  working: 'Working',
  leave: 'On Leave',
};
const SLOTS = [
  { key: 'morning', label: 'Morning', range: '6:00 AM – 12:00 PM', start: 6, end: 12 },
  { key: 'afternoon', label: 'Afternoon', range: '12:00 PM – 6:00 PM', start: 12, end: 18 },
  { key: 'night', label: 'Night', range: '6:00 PM – 12:00 AM', start: 18, end: 24 },
];

const USERS_QUERY = `
  query AvailabilityUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id firstName lastName email roles jobTitle typeOfWork
        employeeCode branchId isActive
      }
      totalPages
    }
  }
`;

const CUSTOMERS_QUERY = `
  query AvailabilityCustomers {
    getCustomer { id fullName email phone address status }
  }
`;

const BRANCHES_QUERY = `
  query AvailabilityBranches {
    getMyCompanyBranches { id name }
  }
`;

const WEEKLY_QUERY = `
  query GetWeeklyAvailability(
    $subjectType: WeeklyAvailabilitySubjectType!
    $subjectId: String!
    $weekStart: String!
  ) {
    getWeeklyAvailability(
      subjectType: $subjectType
      subjectId: $subjectId
      weekStart: $weekStart
    ) {
      id slots preferredHours maxHours
    }
  }
`;

const UPSERT_WEEKLY = `
  mutation UpsertWeeklyAvailability($input: UpsertWeeklyAvailabilityInput!) {
    upsertWeeklyAvailability(input: $input) {
      id slots preferredHours maxHours
    }
  }
`;

/** Portal calendar availability (cleaner / manager / contractor). */
const CALENDAR_RANGE_QUERY = `
  query AdminPortalAvailability(
    $userId: String!
    $startDate: String!
    $endDate: String!
  ) {
    getAvailabilityByUserAndDateRange(
      userId: $userId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      date
      type
      mode
      startTime
      endTime
    }
  }
`;

const ADMIN_BULK_SET = `
  mutation AdminBulkSetAvailability(
    $userId: String!
    $input: BulkAvailabilityInput!
  ) {
    adminBulkSetAvailability(userId: $userId, input: $input) {
      id
      date
      type
    }
  }
`;

const LEAVES_QUERY = `
  query AvailabilityLeaves {
    leaveRequests {
      id name employeeId leaveType status startDate endDate
    }
  }
`;

const PROJECTS_QUERY = `
  query AvailabilityProjects {
    findAllProject {
      id
      name
      status
      startDate
      deadLine
      startTime
      endTime
      booking { id address fullName }
      manager { id }
      tasks {
        id
        title
        location
        date
        time
        user { id }
      }
    }
  }
`;

const TIME_CARDS_RANGE_QUERY = `
  query AvailabilityTimeCards($date: String) {
    findTimeCards(date: $date) {
      id userId date timeIn timeOut duration notes leaveType
    }
  }
`;

function emptySlots() {
  return {
    morning: [null, null, null, null, null, null, null],
    afternoon: [null, null, null, null, null, null, null],
    night: [null, null, null, null, null, null, null],
  };
}

/** Prefer YYYY-MM-DD from API date strings (avoid TZ shifting date-only values). */
function dateKeyFromValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) return isoDay[1];
  const d = parseDay(value);
  return d ? toISODate(d) : '';
}

/** Map one portal calendar record onto morning/afternoon/night for a day. */
function applyCalendarRecordToDay(slots, dayIndex, record) {
  if (!record || dayIndex < 0) return;
  const type = String(record.type || '').toLowerCase();
  const mode = String(record.mode || '').toLowerCase();

  if (type === 'available') {
    slots.morning[dayIndex] = 'available';
    slots.afternoon[dayIndex] = 'available';
    slots.night[dayIndex] = 'available';
    return;
  }

  if (type !== 'unavailable') {
    // Unknown / missing type — treat like portal default (available)
    slots.morning[dayIndex] = 'available';
    slots.afternoon[dayIndex] = 'available';
    slots.night[dayIndex] = 'available';
    return;
  }

  if (mode === 'custom_hours') {
    const start = hourFromClock(record.startTime);
    const end = hourFromClock(record.endTime);
    const blocked = new Set(slotKeysForTimeRange(start, end));
    SLOTS.forEach((slot) => {
      slots[slot.key][dayIndex] = blocked.has(slot.key)
        ? 'unavailable'
        : 'available';
    });
    return;
  }

  // full day unavailable
  slots.morning[dayIndex] = 'unavailable';
  slots.afternoon[dayIndex] = 'unavailable';
  slots.night[dayIndex] = 'unavailable';
}

/**
 * Mirror employee-portal Calendar.jsx:
 * - explicit available → Available
 * - unavailable (full / partial) → Unavailable
 * - no calendar row → Available (portal paints these green by default)
 */
function mapCalendarToSlots(records, weekDates) {
  const slots = emptySlots();
  const byDate = new Map();
  (records || []).forEach((r) => {
    const key = dateKeyFromValue(r.date);
    if (!key) return;
    byDate.set(key, r);
  });
  weekDates.forEach((day, idx) => {
    const key = toISODate(day);
    const rec = byDate.get(key);
    if (rec) {
      applyCalendarRecordToDay(slots, idx, rec);
    } else {
      // Same as portal: unmarked days show as available
      slots.morning[idx] = 'available';
      slots.afternoon[idx] = 'available';
      slots.night[idx] = 'available';
    }
  });
  return slots;
}

/**
 * Convert editable week slots into portal calendar bulk payloads
 * (one Availability row per date — same model as employee portal calendar).
 */
function slotsToCalendarPayloads(slots, weekDates) {
  /** @type {Array<{ type: string, dates: string[], mode?: string, startTime?: string, endTime?: string }>} */
  const payloads = [];

  weekDates.forEach((day, idx) => {
    const values = [
      slots.morning[idx],
      slots.afternoon[idx],
      slots.night[idx],
    ];
    const filled = values.filter(Boolean);
    if (!filled.length) return;

    const date = toISODate(day);
    const hasUnavail = filled.some((v) => v === 'unavailable');
    const allUnavail = values.every((v) => v === 'unavailable');
    const allOpen = filled.every((v) => v === 'available');

    if (allUnavail) {
      payloads.push({
        type: 'UNAVAILABLE',
        dates: [date],
        mode: 'FULL_DAY',
      });
      return;
    }

    if (hasUnavail) {
      const blockedSlots = SLOTS.filter((_, i) => values[i] === 'unavailable');
      const startH = blockedSlots[0]?.start ?? 9;
      const endH = blockedSlots[blockedSlots.length - 1]?.end ?? 17;
      payloads.push({
        type: 'UNAVAILABLE',
        dates: [date],
        mode: 'CUSTOM_HOURS',
        startTime: `${String(startH).padStart(2, '0')}:00`,
        endTime:
          endH >= 24 ? '23:59' : `${String(endH).padStart(2, '0')}:00`,
      });
      return;
    }

    if (allOpen) {
      payloads.push({
        type: 'AVAILABLE',
        dates: [date],
      });
    }
  });

  return payloads;
}

function mondayOf(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function parseDay(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function hourFromClock(value) {
  if (value == null || value === '') return null;
  const match = String(value).trim().match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) + Number(match[2]) / 60;
}

function slotKeysForTimeRange(startH, endH) {
  if (startH == null) return ['morning', 'afternoon', 'night'];
  const end = endH == null || endH <= startH ? startH + 1 : endH;
  const keys = [];
  SLOTS.forEach((slot) => {
    // overlap between [startH, end) and [slot.start, slot.end)
    if (startH < slot.end && end > slot.start) keys.push(slot.key);
  });
  return keys.length ? keys : ['morning'];
}

function parseTaskHours(taskTime, projectStart, projectEnd) {
  const raw = String(taskTime || '').trim();
  if (raw.includes('-')) {
    const [a, b] = raw.split('-').map((p) => p.trim());
    return { start: hourFromClock(a), end: hourFromClock(b) };
  }
  const single = hourFromClock(raw);
  if (single != null) return { start: single, end: single + 2 };
  return {
    start: hourFromClock(projectStart),
    end: hourFromClock(projectEnd),
  };
}

function normalizeRole(role) {
  return String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');
}

function hasRole(roles = [], expected = []) {
  const set = expected.map(normalizeRole);
  return (roles || []).some((r) => set.includes(normalizeRole(r)));
}

function personKind(roles = []) {
  if (hasRole(roles, ['SCHEDULER_DISPATCHER', 'CONTRACTOR'])) return 'contractor';
  if (hasRole(roles, ['OPERATIONS_MANAGER', 'SUPERVISOR'])) return 'manager';
  if (hasRole(roles, ['FIELD_CLEANER', 'CLEANER'])) return 'cleaner';
  if (hasRole(roles, ['ADMIN', 'SUPER_ADMIN', 'BRANCH_SUPER_ADMIN'])) return 'admin';
  return 'staff';
}

function titleOf(u) {
  const kind = personKind(u.roles);
  if (kind === 'contractor') return 'Contractor';
  if (kind === 'manager') return u.jobTitle || 'Supervisor';
  if (kind === 'cleaner') return 'Cleaner';
  if (kind === 'admin') return 'Admin';
  return u.jobTitle || 'Staff';
}

function initials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || '?'
  );
}

function avatarColor(name) {
  const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4'];
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 17) % colors.length;
  return colors[h];
}

function nextEditState(current) {
  const editable = EDIT_STATES.includes(current) ? current : null;
  const idx = EDIT_STATES.indexOf(editable);
  return EDIT_STATES[(idx + 1) % EDIT_STATES.length];
}

function projectActiveInWeek(project, weekStart, weekEnd) {
  const status = String(project?.status || '').toLowerCase();
  if (status.includes('complete') || status.includes('cancel')) return false;
  const start = parseDay(project.startDate) || parseDay(project.deadLine);
  const end = parseDay(project.deadLine) || start;
  if (!start) return true;
  const e = end || start;
  return start <= weekEnd && e >= weekStart;
}

/**
 * Build maps:
 * leaveByDayIndex: Map<dayIndex, { leaveType }>
 * workByCell: Map<`${slotKey}-${dayIndex}`, { title, shift }>
 */
function buildWeekOverlays({
  person,
  weekDates,
  leaves,
  projects,
  timeCardsByDate,
}) {
  const leaveByDay = new Map();
  const workByCell = new Map();

  if (!person || person.subjectType !== 'user') {
    return { leaveByDay, workByCell };
  }

  const keys = [
    String(person.id || '').toLowerCase(),
    String(person.employeeCode || '').toLowerCase(),
    String(person.name || '').toLowerCase(),
  ].filter(Boolean);

  const matchesPerson = (leave) => {
    const candidates = [
      String(leave.employeeId || '').toLowerCase().trim(),
      String(leave.name || '').toLowerCase().trim(),
    ].filter(Boolean);
    if (candidates.some((c) => keys.includes(c))) return true;
    // Fuzzy: leave name contains staff name or vice versa
    const personName = String(person.name || '').toLowerCase().trim();
    if (!personName) return false;
    return candidates.some(
      (c) => c.includes(personName) || personName.includes(c),
    );
  };

  (leaves || []).forEach((leave) => {
    if (String(leave.status || '').toLowerCase() !== 'approved') return;
    if (!matchesPerson(leave)) return;
    const start = parseDay(leave.startDate);
    const end = parseDay(leave.endDate) || start;
    if (!start) return;
    weekDates.forEach((day, idx) => {
      if (day >= start && day <= end) {
        leaveByDay.set(idx, {
          leaveType: leave.leaveType || 'Leave',
        });
      }
    });
  });

  const weekStart = weekDates[0];
  const weekEnd = weekDates[6];

  const pushWork = (dayIndex, slotKey, info) => {
    const key = `${slotKey}-${dayIndex}`;
    if (!workByCell.has(key)) workByCell.set(key, info);
  };

  (projects || []).forEach((project) => {
    if (!projectActiveInWeek(project, weekStart, weekEnd)) return;

    // Only assigned tasks/shifts count as Working (not merely being project manager)
    (project.tasks || []).forEach((task) => {
      if (task?.user?.id !== person.id) return;
      const taskDateKey = dateKeyFromValue(task.date);
      const taskDay = taskDateKey
        ? parseDay(taskDateKey)
        : parseDay(task.date) || parseDay(project.startDate) || null;

      if (!taskDay && !taskDateKey) {
        // Undated task — only mark days the project actually spans this week
        const pStart = parseDay(project.startDate) || weekStart;
        const pEnd = parseDay(project.deadLine) || pStart;
        const hours = parseTaskHours(
          task.time,
          project.startTime,
          project.endTime,
        );
        const slotKeys = slotKeysForTimeRange(hours.start, hours.end);
        weekDates.forEach((day, idx) => {
          if (day < pStart || day > pEnd) return;
          slotKeys.forEach((slotKey) => {
            pushWork(idx, slotKey, {
              title: task.title || project.name || 'Shift',
              shift: task.time || 'Shift',
            });
          });
        });
        return;
      }

      const dayIndex = weekDates.findIndex((d) => {
        if (taskDateKey) return toISODate(d) === taskDateKey;
        return sameDay(d, taskDay);
      });
      if (dayIndex < 0) return;
      const hours = parseTaskHours(task.time, project.startTime, project.endTime);
      slotKeysForTimeRange(hours.start, hours.end).forEach((slotKey) => {
        pushWork(dayIndex, slotKey, {
          title: task.title || project.name || 'Shift',
          shift: task.time || 'Shift',
        });
      });
    });
  });

  // Time cards for the week → mark Working on clocked slots
  weekDates.forEach((day, idx) => {
    const iso = toISODate(day);
    const cards = timeCardsByDate.get(iso) || [];
    cards
      .filter((c) => c.userId === person.id)
      .forEach((card) => {
        if (card.leaveType) {
          leaveByDay.set(idx, {
            leaveType: card.leaveType || 'Leave',
          });
          return;
        }
        const start = hourFromClock(card.timeIn) ?? 9;
        const endRaw = hourFromClock(card.timeOut);
        const end =
          endRaw == null || endRaw === start ? start + 4 : endRaw;
        slotKeysForTimeRange(start, end).forEach((slotKey) => {
          pushWork(idx, slotKey, {
            title: 'Clocked shift',
            shift:
              card.timeIn && card.timeOut
                ? `${card.timeIn}-${card.timeOut}`
                : card.timeIn || 'Shift',
          });
        });
      });
  });

  return { leaveByDay, workByCell };
}

export default function AvailabilityManagement() {
  const { get, patch } = useAdminPageSearch();
  const urlMember = get('member');
  const [people, setPeople] = useState([]);
  const [autoId, setAutoId] = useState('');
  const selectedId = urlMember || autoId;
  const [typeFilter, setTypeFilter] = useState('all');
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [slots, setSlots] = useState(emptySlots);
  const [preferredHours, setPreferredHours] = useState(32);
  const [maxHours, setMaxHours] = useState(40);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeCardsByDate, setTimeCardsByDate] = useState(() => new Map());

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekStartISO = toISODate(weekStart);

  const loadPeople = useCallback(async () => {
    setLoading(true);
    try {
      const [usersAll, customersData, branchesData, leavesData, projectsData] =
        await Promise.all([
          (async () => {
            const all = [];
            let page = 1;
            let totalPages = 1;
            do {
              const data = await graphqlRequest(USERS_QUERY, {
                page,
                limit: 100,
              });
              const result = data?.getAllUser;
              all.push(...(result?.data || []));
              totalPages = result?.totalPages || 1;
              page += 1;
            } while (page <= totalPages && page <= 20);
            return all;
          })(),
          graphqlRequest(CUSTOMERS_QUERY).catch(() => ({ getCustomer: [] })),
          graphqlRequest(BRANCHES_QUERY).catch(() => ({
            getMyCompanyBranches: [],
          })),
          graphqlRequest(LEAVES_QUERY).catch(() => ({ leaveRequests: [] })),
          graphqlRequest(PROJECTS_QUERY).catch(() => ({ findAllProject: [] })),
        ]);

      const bMap = new Map();
      (branchesData?.getMyCompanyBranches || []).forEach((b) => {
        if (b?.id) bMap.set(b.id, b.name || 'Branch');
      });

      setLeaves(leavesData?.leaveRequests || []);
      setProjects(projectsData?.findAllProject || []);

      const users = (usersAll || [])
        .filter((u) => u.isActive !== false)
        .map((u) => {
          const name =
            `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
          const kind = personKind(u.roles);
          return {
            id: u.id,
            subjectType: 'user',
            name,
            title: titleOf(u),
            kind,
            meta: bMap.get(u.branchId) || '—',
            employmentType:
              kind === 'contractor'
                ? 'Contractor'
                : String(u.typeOfWork || '')
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Full-Time',
            active: u.isActive !== false,
            avatar: avatarColor(name),
            initials: initials(name),
            employeeCode: u.employeeCode || '',
          };
        });

      const customers = (customersData?.getCustomer || []).map((c) => ({
        id: c.id,
        subjectType: 'customer',
        name: c.fullName || c.email || 'Client',
        title: 'Client',
        kind: 'client',
        meta: c.address || c.email || '—',
        employmentType: 'Client',
        active: String(c.status || '').toLowerCase() !== 'inactive',
        avatar: avatarColor(c.fullName || c.email),
        initials: initials(c.fullName || c.email),
        employeeCode: '',
      }));

      const merged = [...users, ...customers].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      setPeople(merged);
      setAutoId((prev) => prev || merged[0]?.id || '');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load people');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPeople();
  }, [loadPeople]);

  // Load time cards for each day in the visible week (for Working overlay)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          weekDates.map((day) =>
            graphqlRequest(TIME_CARDS_RANGE_QUERY, {
              date: toISODate(day),
            }).catch(() => ({ findTimeCards: [] })),
          ),
        );
        if (cancelled) return;
        const map = new Map();
        weekDates.forEach((day, i) => {
          map.set(toISODate(day), results[i]?.findTimeCards || []);
        });
        setTimeCardsByDate(map);
      } catch {
        if (!cancelled) setTimeCardsByDate(new Map());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekDates]);

  const filteredPeople = useMemo(() => {
    // "All" = every portal staff member (cleaner / manager / supervisor / contractor)
    const staffOnly = people.filter((p) => p.subjectType === 'user');
    if (typeFilter === 'all') return staffOnly;
    if (typeFilter === 'employee') {
      return staffOnly.filter((p) =>
        ['cleaner', 'manager', 'admin', 'staff'].includes(p.kind),
      );
    }
    if (typeFilter === 'cleaner') {
      return staffOnly.filter((p) => p.kind === 'cleaner');
    }
    if (typeFilter === 'manager') {
      return staffOnly.filter((p) => p.kind === 'manager');
    }
    if (typeFilter === 'contractor') {
      return staffOnly.filter((p) => p.kind === 'contractor');
    }
    if (typeFilter === 'client') {
      return people.filter((p) => p.kind === 'client');
    }
    return staffOnly;
  }, [people, typeFilter]);

  const selected = useMemo(
    () =>
      people.find((p) => p.id === selectedId) ||
      (urlMember ? null : filteredPeople[0] || null),
    [people, selectedId, filteredPeople, urlMember],
  );

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      try {
        const endISO = toISODate(weekDates[6]);

        let preferred = 32;
        let max = 40;
        let weeklySlots = emptySlots();
        try {
          const weeklyData = await graphqlRequest(WEEKLY_QUERY, {
            subjectType: selected.subjectType,
            subjectId: selected.id,
            weekStart: weekStartISO,
          });
          if (cancelled) return;
          const row = weeklyData?.getWeeklyAvailability;
          preferred = Number(row?.preferredHours ?? 32);
          max = Number(row?.maxHours ?? 40);
          if (row?.slots) {
            for (const key of ['morning', 'afternoon', 'night']) {
              if (Array.isArray(row.slots[key])) {
                weeklySlots[key] = Array.from({ length: 7 }, (_, i) => {
                  const v = row.slots[key][i];
                  // Legacy "recurring" treated as available
                  if (v === 'available' || v === 'recurring') return 'available';
                  if (v === 'unavailable') return 'unavailable';
                  return null;
                });
              }
            }
          }
        } catch {
          /* weekly prefs optional */
        }

        let next = emptySlots();
        if (selected.subjectType === 'user') {
          try {
            const calData = await graphqlRequest(CALENDAR_RANGE_QUERY, {
              userId: selected.id,
              startDate: weekStartISO,
              endDate: endISO,
            });
            if (cancelled) return;
            next = mapCalendarToSlots(
              calData?.getAvailabilityByUserAndDateRange || [],
              weekDates,
            );
          } catch (err) {
            console.warn('Date-range availability failed, trying month:', err);
            const months = new Set(
              weekDates.map((d) => `${d.getFullYear()}-${d.getMonth() + 1}`),
            );
            const records = [];
            for (const key of months) {
              const [y, m] = key.split('-').map(Number);
              const monthData = await graphqlRequest(
                `
                query AdminPortalAvailabilityMonth(
                  $userId: String!
                  $year: Int!
                  $month: Int!
                ) {
                  getAvailabilityByUserAndMonth(
                    userId: $userId
                    year: $year
                    month: $month
                  ) {
                    id date type mode startTime endTime
                  }
                }
              `,
                { userId: selected.id, year: y, month: m },
              );
              records.push(
                ...(monthData?.getAvailabilityByUserAndMonth || []),
              );
            }
            if (cancelled) return;
            next = mapCalendarToSlots(records, weekDates);
          }
        } else {
          // Clients / non-portal subjects use weekly slots only
          next = weeklySlots;
        }

        if (cancelled) return;
        setSlots(next);
        setPreferredHours(preferred);
        setMaxHours(max);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setSlots(emptySlots());
          toast.error(err?.message || 'Failed to load portal availability');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, weekStartISO, weekDates]);

  useEffect(() => {
    if (urlMember) return;
    if (selected && !filteredPeople.some((p) => p.id === selected.id)) {
      setAutoId(filteredPeople[0]?.id || '');
    }
  }, [filteredPeople, selected, urlMember]);

  const { leaveByDay, workByCell } = useMemo(
    () =>
      buildWeekOverlays({
        person: selected,
        weekDates,
        leaves,
        projects,
        timeCardsByDate,
      }),
    [selected, weekDates, leaves, projects, timeCardsByDate],
  );

  /** Final cell view for every staff member:
   *  1) On Leave
   *  2) Working (assigned shift / task / clocked in)
   *  3) Unavailable (explicit portal calendar block)
   *  4) Available (default — same as portal green days / normal work days)
   */
  const displayGrid = useMemo(() => {
    const grid = {
      morning: [],
      afternoon: [],
      night: [],
    };
    const isStaff = selected?.subjectType === 'user';
    for (const slot of SLOTS) {
      grid[slot.key] = weekDates.map((_, dayIndex) => {
        const leave = leaveByDay.get(dayIndex);
        if (leave) {
          return {
            state: 'leave',
            label: 'On Leave',
            detail: leave.leaveType,
            locked: true,
          };
        }
        const work = workByCell.get(`${slot.key}-${dayIndex}`);
        if (work) {
          return {
            state: 'working',
            label: 'Working',
            detail: work.shift || work.title,
            title: work.title,
            locked: true,
          };
        }
        const saved = slots[slot.key][dayIndex];
        if (saved === 'unavailable') {
          return {
            state: 'unavailable',
            label: 'Unavailable',
            detail: '',
            locked: false,
          };
        }
        // Portal default + normal work days → Available
        if (isStaff || saved === 'available') {
          return {
            state: 'available',
            label: 'Available',
            detail: '',
            locked: false,
          };
        }
        return {
          state: null,
          label: '',
          detail: '',
          locked: false,
        };
      });
    }
    return grid;
  }, [weekDates, leaveByDay, workByCell, slots, selected]);

  const stats = useMemo(() => {
    const availableDays = new Set();
    const workingDays = new Set();
    const unavailableDays = new Set();
    for (const slot of SLOTS) {
      (displayGrid[slot.key] || []).forEach((cell, i) => {
        if (cell?.state === 'available') {
          availableDays.add(i);
        }
        if (cell?.state === 'working') workingDays.add(i);
        if (cell?.state === 'unavailable') unavailableDays.add(i);
      });
    }
    // A day that is fully on leave shouldn't count as available
    leaveByDay.forEach((_, i) => availableDays.delete(i));
    return {
      available: availableDays.size,
      working: workingDays.size,
      leave: leaveByDay.size,
      unavailable: unavailableDays.size,
    };
  }, [displayGrid, leaveByDay]);

  const cycleCell = (slotKey, dayIndex) => {
    if (leaveByDay.has(dayIndex)) {
      toast.info('On leave this day — schedule cannot be changed.');
      return;
    }
    if (workByCell.has(`${slotKey}-${dayIndex}`)) {
      toast.info('Working this shift — assigned from roster/timecard.');
      return;
    }
    setSlots((prev) => {
      const next = {
        morning: [...prev.morning],
        afternoon: [...prev.afternoon],
        night: [...prev.night],
      };
      next[slotKey][dayIndex] = nextEditState(next[slotKey][dayIndex]);
      return next;
    });
  };

  const handleSave = async () => {
    if (!selected) {
      toast.error('Select a person first');
      return;
    }
    try {
      setSaving(true);

      if (selected.subjectType === 'user') {
        const payloads = slotsToCalendarPayloads(slots, weekDates);
        for (const payload of payloads) {
          await graphqlRequest(ADMIN_BULK_SET, {
            userId: selected.id,
            input: payload,
          });
        }
      }

      const normalizeWeeklySlot = (v) => {
        if (v === 'available' || v === 'unavailable') return v;
        return null;
      };
      await graphqlRequest(UPSERT_WEEKLY, {
        input: {
          subjectType: selected.subjectType,
          subjectId: selected.id,
          weekStart: weekStartISO,
          slots: {
            morning: slots.morning.map(normalizeWeeklySlot),
            afternoon: slots.afternoon.map(normalizeWeeklySlot),
            night: slots.night.map(normalizeWeeklySlot),
          },
          preferredHours: Number(preferredHours) || 0,
          maxHours: Number(maxHours) || 0,
        },
      });

      toast.success(
        selected.subjectType === 'user'
          ? 'Availability saved — synced with their portal calendar.'
          : 'Preferences saved.',
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const weekLabel = `${weekDates[0].toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })} – ${weekDates[6].toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })}`;

  return (
    <div className="av-page">
      <header className="av-header">
        <div>
          <h1>Availability</h1>
          <p>
            For every cleaner, manager, supervisor, and contractor: days are
            Available by default (as on their portal calendar), unless they have
            leave, an assigned shift/task, or marked themselves unavailable.
          </p>
        </div>
        <button
          type="button"
          className="av-btn-primary"
          onClick={() => void handleSave()}
          disabled={saving || !selected}
        >
          <FiSave /> {saving ? 'Saving…' : 'Save Availability'}
        </button>
      </header>

      <div className="av-filters">
        {['all', 'cleaner', 'manager', 'contractor'].map((t) => (
          <button
            key={t}
            type="button"
            className={`av-chip${typeFilter === t ? ' is-active' : ''}`}
            onClick={() => setTypeFilter(t)}
          >
            {t === 'all'
              ? 'All Staff'
              : t === 'cleaner'
                ? 'Cleaners'
                : t === 'manager'
                  ? 'Managers / Supervisors'
                  : 'Contractors'}
          </button>
        ))}
        <select
          className="av-person-select"
          value={selected?.id || ''}
          onChange={(e) => patch({ member: e.target.value })}
          disabled={loading || filteredPeople.length === 0}
        >
          {filteredPeople.length === 0 ? (
            <option value="">No people found</option>
          ) : (
            filteredPeople.map((p) => (
              <option key={`${p.subjectType}-${p.id}`} value={p.id}>
                {p.name} · {p.title}
              </option>
            ))
          )}
        </select>
      </div>

      {selected ? (
        <section className="av-person-card">
          <div className="av-avatar" style={{ background: selected.avatar }}>
            {selected.initials}
          </div>
          <div className="av-person-text">
            <h2>{selected.name}</h2>
            <p>
              {selected.title} · {selected.meta}
            </p>
          </div>
          <div className="av-person-badges">
            <span className="av-badge av-badge--muted">
              {selected.employmentType}
            </span>
            <span
              className={`av-badge ${
                selected.active ? 'av-badge--ok' : 'av-badge--off'
              }`}
            >
              ● {selected.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </section>
      ) : null}

      <section className="av-stats">
        <article className="av-stat av-stat--green">
          <span>Available</span>
          <strong>{stats.available} days this week</strong>
        </article>
        <article className="av-stat av-stat--blue">
          <span>Working (Shifts)</span>
          <strong>{stats.working} days this week</strong>
        </article>
        <article className="av-stat av-stat--amber">
          <span>On Leave</span>
          <strong>{stats.leave} days this week</strong>
        </article>
        <article className="av-stat av-stat--grey">
          <span>Unavailable</span>
          <strong>{stats.unavailable} days this week</strong>
        </article>
      </section>

      <section className="av-grid-card">
        <div className="av-grid-toolbar">
          <div className="av-week-nav">
            <button
              type="button"
              aria-label="Previous week"
              onClick={() => setWeekStart((w) => addDays(w, -7))}
            >
              <FiChevronLeft />
            </button>
            <strong>{weekLabel}</strong>
            <button
              type="button"
              aria-label="Next week"
              onClick={() => setWeekStart((w) => addDays(w, 7))}
            >
              <FiChevronRight />
            </button>
          </div>
          <div className="av-legend">
            <span>
              <i className="av-dot av-dot--available" /> Available
            </span>
            <span>
              <i className="av-dot av-dot--working" /> Working
            </span>
            <span>
              <i className="av-dot av-dot--leave" /> On Leave
            </span>
            <span>
              <i className="av-dot av-dot--unavailable" /> Unavailable
            </span>
            <em>
              Priority: On Leave → Working (shift/task) → Unavailable → Available
              (default work days). Click a cell to toggle Available / Unavailable.
            </em>
          </div>
        </div>

        <div className="av-grid">
          <div className="av-grid-row av-grid-head">
            <div className="av-grid-corner" />
            {weekDates.map((d) => (
              <div key={toISODate(d)} className="av-grid-dayhead">
                <strong>
                  {d.toLocaleDateString('en-AU', { weekday: 'short' })}
                </strong>
                <span>
                  {d.toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
              </div>
            ))}
          </div>

          {SLOTS.map((slot) => (
            <div key={slot.key} className="av-grid-row">
              <div className="av-grid-slot">
                <strong>{slot.label}</strong>
                <span>{slot.range}</span>
              </div>
              {weekDates.map((_, dayIndex) => {
                const cell = displayGrid[slot.key][dayIndex];
                const state = cell?.state;
                return (
                  <button
                    key={`${slot.key}-${dayIndex}`}
                    type="button"
                    className={`av-cell${state ? ` is-${state}` : ' is-empty'}${
                      cell?.locked ? ' is-locked' : ''
                    }`}
                    onClick={() => cycleCell(slot.key, dayIndex)}
                    title={
                      cell?.title
                        ? `${cell.label}: ${cell.title}`
                        : cell?.detail
                          ? `${cell.label}: ${cell.detail}`
                          : cell?.label || 'Set availability'
                    }
                  >
                    {state ? (
                      <span className="av-cell-text">
                        <strong>{cell.label}</strong>
                        {cell.detail ? <small>{cell.detail}</small> : null}
                      </span>
                    ) : (
                      <FiPlus className="av-cell-plus" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      <section className="av-hours">
        <article className="av-hours-card">
          <h3>Preferred Hours</h3>
          <p>Ideal weekly hours for this person.</p>
          <div className="av-hours-value">{preferredHours}</div>
          <input
            type="range"
            min={0}
            max={60}
            value={preferredHours}
            onChange={(e) => setPreferredHours(Number(e.target.value))}
          />
          <div className="av-hours-ends">
            <span>0h</span>
            <span>60h</span>
          </div>
        </article>
        <article className="av-hours-card">
          <h3>Maximum Hours</h3>
          <p>Hard cap per week including overtime.</p>
          <div className="av-hours-value">{maxHours}</div>
          <input
            type="range"
            min={0}
            max={80}
            value={maxHours}
            onChange={(e) => setMaxHours(Number(e.target.value))}
          />
          <div className="av-hours-ends">
            <span>0h</span>
            <span>80h</span>
          </div>
        </article>
      </section>
    </div>
  );
}
